"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.JwtBase = void 0;
const jsonwebtoken_1 = __importDefault(require("jsonwebtoken"));
const ethers_1 = require("ethers");
const base64url_1 = __importDefault(require("base64url"));
const keys_1 = require("@ew-did-registry/keys");
const utils_1 = require("ethers/lib/utils");
const types_1 = require("./types");
const JwtVerificationFailed_1 = require("./JwtVerificationFailed");
const { arrayify, keccak256, hashMessage, recoverPublicKey } = ethers_1.utils;
/**
 * @description Any JWT implementation verifies any JWT
 */
class JwtBase {
    /**
     * If the signature is correct, method returns decoded JWT payload
     *
     * @example
     * ```typescript
     * const issuerKeys = new Keys();
     * const issuer = new JWT(issuerKeys);
     * const verifier = new JWT(new Keys());
     * const payload = {claim: 'test'};
     *
     * const token = await issuer.sign(payload); // signing algorithm is determined by issuer's signer
     * expect(verifier.verify(token), issuerKeys.publicKey, ['ES256K']).to.eql(payload);
     * ```
     * @param {string} token
     * @param {string} publicKey - public key in hexadecimal format in compressed or uncompressed form
     */
    verify(token, publicKey, { algorithms = [types_1.Algorithms.EIP191] } = {}) {
        if (algorithms.includes(types_1.Algorithms.EIP191)) {
            try {
                return this.verifyEIP191(token, publicKey);
            }
            catch (_a) {
                // ignore catch
            }
        }
        if (algorithms.includes(types_1.Algorithms.ES256)) {
            try {
                return this.verifyES256(token, publicKey);
            }
            catch (_b) {
                // ignore catch
            }
        }
        throw new JwtVerificationFailed_1.JwtVerificationFailed();
    }
    /**
     * Return decoded JWT payload without verifying signature
     *
     * @example
     * ```typescript
     * const issuer = new JWT(new Keys());
     * const verifier = new JWT(new Keys());
     * const payload = {claim: 'test'};
     *
     * const token = await issuer.sign(payload, { algorithm: 'ES256K' });
     *
     * expect(verifier.decode(token, {complete: true}).payload).to.eql(payload);
     * ```
     *
     * @param {string} token
     * @param {object} options
     * @returns string | { [key: string]: any }
     */
    decode(token, options) {
        return jsonwebtoken_1.default.decode(token, options) || '';
    }
    verifyES256(token, pubKey) {
        const pemPubKey = (0, keys_1.pubToPem)(pubKey, keys_1.KeyType.Secp256r1);
        return jsonwebtoken_1.default.verify(token, pemPubKey, { algorithms: ['ES256'] });
    }
    verifyEIP191(token, pubKey) {
        if (!pubKey.startsWith('0x')) {
            pubKey = `0x${pubKey}`;
        }
        pubKey = (0, utils_1.computePublicKey)(pubKey);
        const possibleKeys = (message, signature) => [
            arrayify(keccak256(message)),
            arrayify(hashMessage(arrayify(keccak256(message)))),
        ].map((h) => recoverPublicKey(h, signature)); // uncompressed with 0x
        const [encodedHeader, encodedPayload, encodedSignature] = token.split('.');
        const msg = `0x${Buffer.from(`${encodedHeader}.${encodedPayload}`).toString('hex')}`;
        const signature = base64url_1.default.decode(encodedSignature);
        const verified = possibleKeys(msg, signature).find((key) => key === pubKey);
        if (verified) {
            return JSON.parse(base64url_1.default.decode(encodedPayload));
        }
        throw new Error();
    }
}
exports.JwtBase = JwtBase;
//# sourceMappingURL=JwtBase.js.map